/* utility */
export function debounce(fn, delay) {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => {
            fn.apply(this, args);
        }, delay);
    };
}

export function ratelimit(fn, cps) {
    let lastCallAt = 0;
    let delay = 1000 / cps;
    let timer;
    return function (...args) {
        let t = Date.now();
        clearTimeout(timer);
        if (t - lastCallAt > delay) {
            fn.apply(this, args);
            lastCallAt = t;
        } else {
            timer = setTimeout(() => {
                fn.apply(this, args);
                lastCallAt = t;
            }, lastCallAt + delay - t);
        }
    };
}

export function persist(target, name = 'state', rate = 2) {
    let save = ratelimit(() => {
        localStorage.setItem(name, JSON.stringify(target, (key, value) => key.startsWith('_')? undefined : value));
    }, rate);
    onEvent(target, 'on*')(save)
}

export function onEvent(target, prop) {
    return function (fn) {
        var eventName = prop[2].toLowerCase() + prop.substring(3);
        target._listeners ||= {};
        target._listeners[eventName] ||= new Set();
        target._listeners[eventName].add(fn);
        return function () {
            target._listeners[eventName].delete(fn);
        }
    }
}

function notifyListeners(target, prop, value, oldValue) {
    if (target._listeners ){
        if(target._listeners[prop]) {
            target._listeners[prop].forEach((fn) => fn(value, oldValue, prop, target));
        }
        if(target._listeners['*']) {
            target._listeners['*'].forEach((fn) => fn(value, oldValue, prop, target));
        }
    }
}

export function eventEmitter(obj) {

    if (obj._isEventEmitter) return obj;
    let o;

    if (Array.isArray(obj)) {
        o = new Proxy(obj, {
            get(target, prop, receiver) {
                const value = Reflect.get(target, prop, receiver);

                if (typeof prop !== 'symbol' 
                    && prop.startsWith('on') 
                    && prop[2].toUpperCase() === prop[2]
                ) return onEvent(target, prop, receiver);

                // Capture and wrap array-specific methods to notify on changes
                if (typeof value === 'function') {
                    return function (...args) {
                        const result = Array.prototype[prop].apply(o, args);
                        // For methods that modify the array, notify listeners
                        if (['push', 'pop', 'shift', 'unshift', 'splice', 'sort', 'reverse'].includes(prop)) {
                            notifyListeners(target, prop, args, null);
                        }
                        return result;
                    };
                }
                return value;
            },
            set(target, prop, value, receiver) {
                const oldValue = target[prop];
                if (oldValue !== value) {
                    Reflect.set(target, prop, value, receiver);
                    notifyListeners(target, prop, value, oldValue);
                }
                return true;
            }
        });
    } else {
        o = new Proxy(obj,
            {
                get(target, prop, receiver) {
                    const value = Reflect.get(target, prop, receiver);
                    if (typeof prop !== 'symbol' && prop.startsWith('on') && prop[2].toUpperCase() === prop[2]) return onEvent(target, prop, receiver);
                    return value;
                },
                set(target, prop, value, receiver) {
                    var oldValue = target[prop];
                    if (oldValue === value) return true;
                    if (typeof oldValue !== 'undefined' && oldValue._isEventEmitter && !value._isEventEmitter) {
                        value = eventEmitter(value);
                        onEvent(value, 'on*')(() => {
                            notifyListeners(target, prop, value, oldValue);
                        })
                    }
                    const result = Reflect.set(target, prop, value, receiver);
                    notifyListeners(target, prop, value, oldValue);
                    return result;
                }
            });
        o.merge = function (nu) {
            if (nu) {
                Object.keys(nu).forEach(key => {
                    o[key] = nu[key];
                });
            }
            return o;
        }
    }

    o.set = function (nu) {
        const allKeys = new Set([...Object.keys(o), ...Object.keys(nu)]);
        allKeys.forEach(key => {
            o[key] = nu.hasOwnProperty(key) ? nu[key] : undefined;
        });
        return o;
    }

    o._isEventEmitter = true;

    return o;
}

function updateOption(option, valueSet, translation) {
    if (valueSet.length > 1) {
        let nextValueIndex = (valueSet.indexOf(option.value) + 1) % valueSet.length;
        option.innerHTML = `${translation[option.value]} &rArr; ${translation[valueSet[nextValueIndex]]}`;
    }
}

export function bindSelect(select, state) {
    Array.from(select.options).forEach((option, i) => {
        let key = option.dataset.key;
        if (key) {
            let valueSet = option.dataset.vs.split('|');
            let isTF = option.dataset.vs === 'true|false' || option.dataset.vs === 'false|true';
            let translation = JSON.parse(`{${option.dataset.tr}}`.replaceAll("'", '"'));
            updateOption(option, valueSet, translation);            
            state[key] = isTF ? option.value === 'true' : option.value;
            // we follow state changes by this ui
            onEvent(state, 'on' + key)((value, oldValue, prop, target) => {                
                option.value = isTF ? (value ? 'true' : 'false') : value;
                updateOption(option, valueSet, translation);
            })
        }
    });
    select.addEventListener('change', (event) => {
        let option = select.options[select.selectedIndex];
        if (!option.dataset.key) return;
        let valueSet = option.dataset.vs.split('|');
        let isTF = option.dataset.vs === 'true|false' || option.dataset.vs === 'false|true';
        let nextValueIndex = (valueSet.indexOf(option.value) + 1) % valueSet.length;
        state[option.dataset.key] = isTF ? valueSet[nextValueIndex] === 'true' : valueSet[nextValueIndex];
        select.selectedIndex = 0;
    });
}
